import { useState } from 'react';
import { captureException } from '@sentry/react';
import { Button } from 'antd';
import { TbCopy, TbStar, TbStarOff, TbTrash } from 'react-icons/tb';

import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';
import { WorkspaceResourceListControls } from '@/hooks/useWorkspaceResourceListControls';
import CompactListSelectionToolbar from '@/shared/components/lists/CompactListSelectionToolbar';
import DestructiveActionModal from '@/shared/components/modals/DestructiveActionModal';
import { toast } from '@/shared/components/toasts/PlatformToast';
import SmallTooltip from '@/shared/components/tooltips/SmallTooltip';
import { moveWorkspaceResourceToTrash } from '@/shared/components/workspace-resources/optimisticRestore';
import type { RestorableWorkspaceResourceType } from '@/shared/components/workspace-resources/optimisticRestore';

type WorkspaceResourceBulkItemLabel = 'Dashboard' | 'Folder' | 'Investment' | 'List' | 'Memo' | 'Pipeline';

const resourceTypeByItemLabel: Record<WorkspaceResourceBulkItemLabel, RestorableWorkspaceResourceType> = {
    Dashboard: 'dashboard',
    Folder: 'folder',
    Investment: 'investment',
    List: 'list',
    Memo: 'memo',
    Pipeline: 'pipeline',
};

const BULK_TRASH_BATCH_SIZE = 8;

interface WorkspaceResourceListBulkActionsProps<T extends { id: string }> {
    controls: WorkspaceResourceListControls;
    disabled?: boolean;
    getIsFavorite: (item: T) => boolean;
    getPath: (item: T) => string;
    itemLabel: WorkspaceResourceBulkItemLabel;
    items: T[];
    onSetFavorite: (item: T, favorite: boolean) => Promise<void> | void;
}

/** Shared mutation-safe selection toolbar for top-level workspace resource lists. */
const WorkspaceResourceListBulkActions = <T extends { id: string }>({
    controls,
    disabled = false,
    getIsFavorite,
    getPath,
    itemLabel,
    items,
    onSetFavorite,
}: WorkspaceResourceListBulkActionsProps<T>): JSX.Element | null => {
    const { token } = useAntDesignTheme();
    const [loading, setLoading] = useState(false);
    const [moveToTrashOpen, setMoveToTrashOpen] = useState(false);
    const selectedItems = items.filter((item) => controls.isSelected(item.id));
    const allFavorited = selectedItems.length > 0 && selectedItems.every(getIsFavorite);

    const copyLinks = async () => {
        try {
            const links = selectedItems.map((item) => new URL(getPath(item), window.location.origin).toString());
            await navigator.clipboard.writeText(links.join('\n'));
            toast('Links Copied', {
                description: `${links.length} ${itemLabel.toLowerCase()} link${links.length === 1 ? '' : 's'} copied.`,
            });
        } catch (error) {
            captureException(error, {
                extra: {
                    action: 'workspace-resource-list-copy-links',
                    itemLabel,
                    selectedCount: selectedItems.length,
                },
            });
            toast.error('Unable to Copy Resource Links');
        }
    };

    const runAction = async (action: (item: T) => Promise<void> | void) => {
        if (loading || selectedItems.length === 0) {
            return;
        }

        try {
            setLoading(true);
            await Promise.all(selectedItems.map((item) => action(item)));
            controls.toggleAll(false);
        } catch (error) {
            captureException(error, {
                extra: {
                    action: 'workspace-resource-list-bulk-action',
                    itemLabel,
                    selectedCount: selectedItems.length,
                },
            });
        } finally {
            setLoading(false);
        }
    };

    const moveSelectedToTrash = async () => {
        if (loading || selectedItems.length === 0) {
            return;
        }

        setLoading(true);

        try {
            const results: PromiseSettledResult<unknown>[] = [];
            const resourceType = resourceTypeByItemLabel[itemLabel];

            for (let index = 0; index < selectedItems.length; index += BULK_TRASH_BATCH_SIZE) {
                const batch = selectedItems.slice(index, index + BULK_TRASH_BATCH_SIZE);

                results.push(
                    ...(await Promise.allSettled(
                        batch.map((item) => moveWorkspaceResourceToTrash(resourceType, item.id)),
                    )),
                );
            }

            const failedItems = selectedItems.filter((_, index) => results[index]?.status === 'rejected');
            const succeededCount = selectedItems.length - failedItems.length;

            controls.toggleAll(false);
            failedItems.forEach((item) => controls.toggleSelected(item.id, true));
            results.forEach((result, index) => {
                if (result.status === 'rejected') {
                    captureException(result.reason, {
                        extra: {
                            action: 'workspace-resource-list-bulk-trash',
                            itemLabel,
                            resourceId: selectedItems[index]?.id,
                        },
                    });
                }
            });

            if (failedItems.length > 0) {
                toast.error(
                    `Unable to Move ${failedItems.length} ${itemLabel}${failedItems.length === 1 ? '' : 's'} to Trash`,
                    {
                        description:
                            succeededCount > 0
                                ? `${succeededCount} ${itemLabel.toLowerCase()}${succeededCount === 1 ? '' : 's'} moved successfully. Failed items remain selected.`
                                : 'No resources were moved. Failed items remain selected.',
                    },
                );
            } else {
                toast.warning(
                    `${selectedItems.length} ${itemLabel}${selectedItems.length === 1 ? '' : 's'} Moved to Trash`,
                );
            }
        } finally {
            setLoading(false);
        }
    };

    return (
        <>
            <CompactListSelectionToolbar
                disabled={disabled}
                itemLabel={itemLabel}
                loading={loading}
                onClear={() => controls.toggleAll(false)}
                onSelectAll={() => controls.toggleAll(true)}
                selectedCount={controls.selectedCount}
                visibleCount={controls.itemCount}
            >
                <SmallTooltip title='Copy Links'>
                    <Button
                        aria-label='Copy links to selected items'
                        disabled={disabled || loading}
                        icon={<TbCopy style={{ color: token.colorTextTertiary }} />}
                        onClick={() => void copyLinks()}
                    />
                </SmallTooltip>
                <SmallTooltip title={allFavorited ? 'Remove from Favorites' : 'Add to Favorites'}>
                    <Button
                        aria-label={
                            allFavorited ? 'Remove selected items from favorites' : 'Add selected items to favorites'
                        }
                        disabled={disabled || loading}
                        icon={
                            allFavorited ? (
                                <TbStarOff style={{ color: token.colorTextTertiary }} />
                            ) : (
                                <TbStar style={{ color: token.colorTextTertiary }} />
                            )
                        }
                        onClick={() => void runAction((item) => onSetFavorite(item, !allFavorited))}
                    />
                </SmallTooltip>
                <SmallTooltip title='Move to Trash'>
                    <Button
                        aria-label='Move selected items to Trash'
                        danger
                        disabled={disabled || loading}
                        icon={<TbTrash />}
                        onClick={() => setMoveToTrashOpen(true)}
                    />
                </SmallTooltip>
            </CompactListSelectionToolbar>
            <DestructiveActionModal
                actionLabel='Move to Trash'
                alertDescription={`The ${selectedItems.length} selected ${itemLabel.toLowerCase()}${selectedItems.length === 1 ? '' : 's'} will be removed from this view and can be restored from Trash.`}
                alertTitle={`Move selected ${itemLabel.toLowerCase()}${selectedItems.length === 1 ? '' : 's'} to Trash?`}
                errorMessage={`Unable to move selected ${itemLabel.toLowerCase()}${selectedItems.length === 1 ? '' : 's'} to Trash`}
                loading={loading}
                onCancel={() => setMoveToTrashOpen(false)}
                onConfirm={moveSelectedToTrash}
                open={moveToTrashOpen}
                severity='warning'
                title={`Move ${selectedItems.length} ${itemLabel}${selectedItems.length === 1 ? '' : 's'} to Trash`}
            />
        </>
    );
};

export default WorkspaceResourceListBulkActions;
